import { z } from 'zod';

import { Box, Form, Stack, Text } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { formatPhoneNumberDisplay } from '@common/utils/formatPhoneNumber';

import { useGetAiBdcSettings } from '../api/useGetAiBdcSettings';

// Read-only placeholder schema (no editable fields)
const readOnlySchema = z.object({});

function SmsChannelSummary(): React.ReactNode {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);

	const isEnabled = settings?.sms?.enabled === true;
	const phoneNumber = settings?.sms?.twilioPhone;

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-sms-channel-summary'
				schema={readOnlySchema}
				defaultValues={{}}
				onSubmit={async () => ({ isSuccess: false })}
				title='Channel status'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='Overview of the current SMS channel configuration. To change these settings, go to General > Channels.'>
				<Stack itemsSpacing='12'>
					<Stack itemsSpacing='4'>
						<Text size='bodySmall' weight='semibold'>
							SMS channel
						</Text>
						<Text size='bodySmall' color={isEnabled ? 'success' : 'secondary'}>
							{isEnabled ? 'Enabled' : 'Disabled'}
						</Text>
					</Stack>
					<Stack itemsSpacing='4'>
						<Text size='bodySmall' weight='semibold'>
							Phone number
						</Text>
						<Text size='bodySmall' color='secondary'>
							{formatPhoneNumberDisplay(phoneNumber) ?? (isEnabled ? 'Provisioning in progress' : 'Not provisioned')}
						</Text>
					</Stack>
				</Stack>
			</Form>
		</Box>
	);
}

function SmsIdentitySummary(): React.ReactNode {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-sms-identity-summary'
				schema={readOnlySchema}
				defaultValues={{}}
				onSubmit={async () => ({ isSuccess: false })}
				title='Identity'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='The identity your AI assistant uses in SMS conversations. To change these settings, go to General > Identity.'>
				<Stack itemsSpacing='12'>
					<Stack itemsSpacing='4'>
						<Text size='bodySmall' weight='semibold'>
							Display name
						</Text>
						<Text size='bodySmall' color='secondary'>
							{settings?.displayName ?? '—'}
						</Text>
					</Stack>
					<Stack itemsSpacing='4'>
						<Text size='bodySmall' weight='semibold'>
							Default language
						</Text>
						<Text size='bodySmall' color='secondary'>
							{settings?.defaultLanguage ?? 'English'}
						</Text>
					</Stack>
				</Stack>
			</Form>
		</Box>
	);
}

function SmsEscalationSummary(): React.ReactNode {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);

	const isEnabled = settings?.skills?.escalation?.enabled === true;

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-sms-escalation-summary'
				schema={readOnlySchema}
				defaultValues={{}}
				onSubmit={async () => ({ isSuccess: false })}
				title='Escalation'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='How the AI hands off SMS conversations when it needs human support. To change these settings, go to General > Escalation.'>
				<Stack itemsSpacing='12'>
					<Stack itemsSpacing='4'>
						<Text size='bodySmall' weight='semibold'>
							Escalation
						</Text>
						<Text size='bodySmall' color={isEnabled ? 'success' : 'secondary'}>
							{isEnabled ? 'Enabled' : 'Disabled'}
						</Text>
					</Stack>
					{isEnabled && settings?.skills?.escalation?.email && (
						<Stack itemsSpacing='4'>
							<Text size='bodySmall' weight='semibold'>
								Escalation email
							</Text>
							<Text size='bodySmall' color='secondary'>
								{settings.skills.escalation.email}
							</Text>
						</Stack>
					)}
				</Stack>
			</Form>
		</Box>
	);
}

export function SmsSettingsSection(): React.JSX.Element {
	return (
		<Stack itemsSpacing='16'>
			<SmsChannelSummary />
			<SmsIdentitySummary />
			<SmsEscalationSummary />
		</Stack>
	);
}
